import React from "react";
import "./AdminTable.scss";

type TableDir = "auto" | "ltr" | "rtl";

type CellDirResolver<T> = TableDir | ((row: T, rowIndex: number) => TableDir | undefined);

export interface AdminTableColumn<T> {
  key: string;
  header: React.ReactNode;
  align?: "left" | "center" | "right";
  headerDir?: TableDir;
  cellDir?: CellDirResolver<T>;
  render: (row: T, rowIndex: number) => React.ReactNode;
}

interface AdminTableProps<T> {
  data: T[];
  columns: AdminTableColumn<T>[];
  rowKey: (row: T, rowIndex: number) => React.Key;
  classNamePrefix?: string;
  containerClassName?: string;
  shadeAlternateRows?: boolean;
  emptyState?: React.ReactNode;
}

const AdminTable = <T,>({
  data,
  columns,
  rowKey,
  classNamePrefix = "admin-common-table",
  containerClassName,
  shadeAlternateRows = true,
  emptyState,
}: AdminTableProps<T>) => {
  const base = classNamePrefix;

  const resolveCellDir = (column: AdminTableColumn<T>, row: T, rowIndex: number): TableDir | undefined => {
    if (!column.cellDir) return undefined;
    if (typeof column.cellDir === "function") {
      return column.cellDir(row, rowIndex);
    }
    return column.cellDir;
  };

  return (
    <div className={containerClassName ? `${base} ${containerClassName}` : base}>
      <div className={`${base}__scroll-wrapper`}>
        <table className={`${base}__element`}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} className={`${base}__col--${column.key}`} />
            ))}
          </colgroup>

          <thead className={`${base}__head`}>
            <tr className={`${base}__row ${base}__row--head`}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${base}__cell${column.align ? ` ${base}__cell--${column.align}` : ""}`}
                  dir={column.headerDir ?? "auto"}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className={`${base}__body`}>
            {data.length === 0 && (
              <tr className={`${base}__empty-row`}>
                <td className={`${base}__empty`} colSpan={columns.length}>
                  {emptyState}
                </td>
              </tr>
            )}
            {data.map((row, rowIndex) => (
              <tr
                key={rowKey(row, rowIndex)}
                className={`${base}__row ${base}__row--body${shadeAlternateRows && rowIndex % 2 !== 0 ? ` ${base}__row--shaded` : ""}`}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${base}__cell${column.align ? ` ${base}__cell--${column.align}` : ""}`}
                    dir={resolveCellDir(column, row, rowIndex)}
                  >
                    {column.render(row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTable;
